// @ts-strict-ignore
import React, {
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Platform } from 'react-native';

import analytics from '../../../modules/analytics';
import type { RootState } from '../../../store/models';
import { selectors } from '../../../store/CurrentUser.model';
import { SearchPopoverModal } from '../search/SearchPopover';

import { Header } from './Header';
import {
  useGetUtmParams,
  useRouteName,
  useResponsiveEffect,
  useLinkTo,
  useMount,
} from '../../../hooks';
import useMobileHeaderSearchIcon from './useMobileHeaderSearchIcon';
import {
  PROFILE_EDIT_ROUTE,
  PROFILE_ROUTE,
  PROVIDER_ONBOARDING_SIGNUP,
} from '../../../route-names';
import { useHeaderReferralEntry } from './useHeaderReferralEntry';
import { unSlugify } from '../../../modules/stringUtils';
import { AuthOrigins } from '../../../modules/user/constants';
import { useShowSubscriptionFeatures } from '../../../hooks/useShowSubscriptionFeatures';

export const HeaderContainer: React.FCWithChildren = () => {
  let isOnProfilePage = false;

  const breakpoints = useResponsiveEffect();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector(selectors.isLoggedIn);
  const providerId = useSelector(selectors.getLoggedInProviderId);
  const stateName = useSelector<RootState, string>(state => state.route.name);
  const utmParams = useGetUtmParams();
  const [canShowSubscriptionFeatures] = useShowSubscriptionFeatures();

  // checking if is in pro profile page;
  const [currentRoute] = useRouteName();
  const linkTo = useLinkTo();
  const isProProfilePage = currentRoute?.startsWith?.(PROFILE_ROUTE);
  const isFromSearch = useSelector<RootState, boolean>(({ search, route }) => search?.source !== ''
  || route?.params?.ncd_source === 'generic_search');
  const searchedService = useSelector<RootState, string>(({ search, route }) => unSlugify(search?.query || route?.params?.q || ''));
  const showHeaderSearchBar = (isProProfilePage
    && (breakpoints.mobile || breakpoints.tablet)
  );
  const headerSearchBarText = (
    searchedService
    && isFromSearch
  ) ? `Search for ${searchedService} on StyleSeat`
    : 'Search on StyleSeat';

  const isMobile = breakpoints.mobile
  || breakpoints.tablet
  || breakpoints.desktopNarrow
  || breakpoints.mobileLayout;

  useMount(() => {
    dispatch.search.loadLastSearchLocation();
  });

  useEffect(() => {
    if (showHeaderSearchBar) {
      analytics.track('viewed', {
        component: 'search_bar',
      });
    }
  }, [showHeaderSearchBar]);

  const {
    showDot,
    showMobileSearch,
    dismissDotAndTrackSearchClick,
  } = useMobileHeaderSearchIcon();

  const [searchModalOpen, setSearchModalOpen] = React.useState<boolean>(false);

  const showSetUpBusinessLink = useMemo<boolean>(() => {
    if (!canShowSubscriptionFeatures) {
      return false;
    }

    const disallowRoutes = [
      'provider_vanity',
      'provider_signup',
      'profile',
    ];
    const isDisallowedRoute = route => stateName.startsWith(route);
    const inDisallowedRoute = disallowRoutes.some(isDisallowedRoute);

    let result: boolean = false;
    if (typeof stateName !== 'string') {
      return result;
    }

    if (!inDisallowedRoute) {
      result = isMobile
       && !breakpoints.mobileNarrow
       && !providerId
       && !showMobileSearch;
    }

    return result;
  }, [
    canShowSubscriptionFeatures,
    stateName,
    isMobile,
    breakpoints.mobileNarrow,
    providerId,
    showMobileSearch,
  ]);

  if (typeof stateName === 'string') {
    if (
      stateName.startsWith(PROFILE_ROUTE)
      || stateName.startsWith(PROFILE_EDIT_ROUTE)
    ) {
      isOnProfilePage = true;
    }
  }

  const handleClickSetUpMyBusiness = useCallback(async () => {
    analytics.track('provider_homepage_set_up_my_business_link_click', {
      origin: 'header_nav',
      platform: Platform.OS,
    });

    linkTo(PROVIDER_ONBOARDING_SIGNUP, {
      source: AuthOrigins.HeaderNav,
    });
  }, [linkTo]);

  const handleMobileSearchClick = useCallback(() => {
    dismissDotAndTrackSearchClick();
    setSearchModalOpen(true);
  }, [dismissDotAndTrackSearchClick]);

  const handleMobileSearchDismiss = useCallback(() => {
    setSearchModalOpen(false);
  }, []);

  const handleDirectNavigation = useCallback((route: string) => {
    const profileRoutes = [
      PROFILE_ROUTE,
      PROFILE_EDIT_ROUTE,
    ];
    if (
      route === stateName
      || (profileRoutes.includes(route) && profileRoutes.includes(stateName))
    ) {
      // Since it's not on its own route, navigation will not touch the search modal if it's
      // summoned from the header and the header is not re-rendered. So if we're navigating on the
      // same screen (most likely the profile), just close the modal.
      setSearchModalOpen(false);
    }
  }, [
    setSearchModalOpen,
    stateName,
  ]);

  const referralShareProps = useHeaderReferralEntry(isOnProfilePage);

  let showReferralEntry = (
    !!referralShareProps
    && (isOnProfilePage || isLoggedIn || !isMobile)
  );

  if ((
    stateName?.startsWith('landing')
      || stateName?.startsWith('home'))
    && isLoggedIn
    && providerId
  ) {
    showReferralEntry = false;
  }

  return (
    <React.Fragment>
      <Header
        providerId={providerId}
        utmParams={utmParams}
        breakpoints={breakpoints}
        referralShareProps={referralShareProps}
        headerSearchBarText={headerSearchBarText}
        showSidebarToggle
        showSearch={isOnProfilePage}
        showMobileBorder={isOnProfilePage}
        showMobileSearchDot={showDot}
        showMobileSearch={showMobileSearch}
        showSetUpBusinessLink={showSetUpBusinessLink}
        showReferralEntry={showReferralEntry}
        showHeaderSearchBar={showHeaderSearchBar}
        onMobileSearchClick={handleMobileSearchClick}
        onClickSetUpMyBusiness={handleClickSetUpMyBusiness}
      />
      {searchModalOpen && (
        <SearchPopoverModal
          source="client_mobile_header_search_btn"
          onClose={handleMobileSearchDismiss}
          onDirectNavigation={handleDirectNavigation}
        />
      )}
    </React.Fragment>
  );
};
